<template>
  <div class="hms-page">
    <Navigation />
    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="moh-back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="m-0"><i class="mdi mdi-file-chart-outline me-2"></i>MoH Monthly Reports</h4>
        </div>
        <div class="d-flex gap-2 align-items-center">
          <select v-model.number="year" class="moh-input" @change="load">
            <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
          </select>
          <select v-model.number="month" class="moh-input" @change="load">
            <option v-for="(m, i) in months" :key="i" :value="i+1">{{ m }}</option>
          </select>
          <button class="moh-btn moh-btn-blue" @click="load"><i class="mdi mdi-refresh"></i> Refresh</button>
        </div>
      </div>

      <div class="moh-tab-row">
        <button v-for="f in forms" :key="f.code" class="moh-tab" :class="{ active: formCode===f.code }" @click="formCode=f.code; load()">
          <i :class="f.icon"></i> {{ f.label }}
        </button>
      </div>

      <!-- Deadline panel (Narok feedback N19) -->
      <div class="moh-deadline-panel" v-if="deadlines.length">
        <div class="moh-deadline-head"><i class="mdi mdi-calendar-alert me-1"></i><strong>Submission deadlines</strong> — reminders fire automatically (3 days before, on the day, and on overdue).</div>
        <div class="moh-deadline-grid">
          <div v-for="d in deadlines" :key="d.deadline_id" class="moh-deadline-card" :class="deadlineClass(d)">
            <div class="moh-deadline-code">{{ d.report_code }}</div>
            <div class="moh-deadline-name">{{ d.report_name }}</div>
            <div class="moh-deadline-due">Due day {{ d.due_day_of_month }} of next month · {{ d.responsible_role }}</div>
            <div class="moh-deadline-state" :class="deadlineClass(d)">{{ deadlineState(d) }}</div>
          </div>
        </div>
      </div>

      <div v-if="data" class="moh-header">
        <div>
          <h3 class="moh-title">{{ data.title }}</h3>
          <div class="moh-muted">Facility report · {{ data.period && data.period.label }}</div>
        </div>
        <div class="moh-actions">
          <span class="moh-pill" :class="lockPill">{{ (data.lock_status || 'draft').replace(/_/g,' ') }}</span>
          <button v-if="data.lock_status === 'draft' || !data.lock_status" class="moh-btn moh-btn-secondary moh-btn-sm" @click="lockForm">
            <i class="mdi mdi-lock-outline"></i> Lock
          </button>
          <button v-if="data.lock_status === 'locked'" class="moh-btn moh-btn-success moh-btn-sm" @click="submitForm">
            <i class="mdi mdi-cloud-upload"></i> Mark submitted
          </button>
          <button class="moh-btn moh-btn-secondary moh-btn-sm" @click="exportCsv">
            <i class="mdi mdi-download"></i> Export CSV
          </button>
          <button class="moh-btn moh-btn-secondary moh-btn-sm" @click="printForm">
            <i class="mdi mdi-printer"></i> Print
          </button>
        </div>
      </div>

      <div v-if="loading" class="moh-empty"><i class="mdi mdi-loading mdi-spin"></i> Loading…</div>

      <div v-if="data && data.sections && !loading">
        <div v-for="s in data.sections" :key="s.key" class="moh-section">
          <div class="moh-section-head">{{ s.label }}</div>
          <table class="moh-table">
            <thead>
              <tr>
                <th style="width:70px">Code</th>
                <th>Indicator</th>
                <th style="width:120px; text-align:right">Value</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in s.rows" :key="r.code">
                <td class="moh-code">{{ r.code }}</td>
                <td>{{ r.label }}</td>
                <td class="moh-value" :class="r.value > 0 ? 'moh-value-has' : 'moh-value-zero'">{{ r.value }}</td>
                <td class="moh-note">{{ r.note || '' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="data" class="moh-footer">
        <p>Generated {{ now }} from live HMS data. All counts are for the period <strong>{{ data.period && data.period.start }}</strong> → <strong>{{ data.period && data.period.end }}</strong>. Lock the form to freeze a snapshot for submission to county / DHIS2.</p>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "MohReports",
  components: { Navigation },
  data() {
    const now = new Date();
    return {
      formCode: 'moh710',
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      loading: false,
      data: null,
      now: now.toLocaleString(),
      forms: [
        { code: 'moh710',  label: '710 — MCH / RH',     icon: 'mdi mdi-mother-heart' },
        { code: 'moh717',  label: '717 — HIV / PMTCT',  icon: 'mdi mdi-virus' },
        { code: 'moh705a', label: '705A — OPD U5',      icon: 'mdi mdi-baby-face' },
        { code: 'moh705b', label: '705B — OPD 5+',      icon: 'mdi mdi-account-group' },
        { code: 'moh718',  label: '718 — TB',           icon: 'mdi mdi-lungs' }
      ],
      months: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
      deadlines: []
    };
  },
  computed: {
    years() {
      const now = new Date().getFullYear();
      const arr = []; for (let y = now; y >= now - 3; y--) arr.push(y);
      return arr;
    },
    lockPill() {
      const s = this.data && this.data.lock_status;
      return ({ draft: 'moh-pill-info', locked: 'moh-pill-warn', submitted_to_county: 'moh-pill-success', submitted_to_national: 'moh-pill-success' })[s] || 'moh-pill-muted';
    }
  },
  mounted() { this.load(); this.loadDeadlines(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const r = await executeJson({ function: 'getMohForm', form_code: this.formCode, year: this.year, month: this.month }, hms);
        if (r.data && r.data.success) this.data = r.data.data;
        else this.data = null;
      } finally { this.loading = false; }
    },
    async loadDeadlines() {
      try {
        const r = await executeJson({ function: 'listMohDeadlines' }, hms);
        if (r.data && r.data.success) this.deadlines = r.data.data.deadlines || [];
      } catch(e) { /* offline ok */ }
    },
    deadlineState(d) {
      const today = new Date(); const dom = today.getDate(); const due = Number(d.due_day_of_month);
      // Reporting period is "last month"; deadlines fire on the due day of the month after.
      if (dom < due - 3) return 'Upcoming';
      if (dom < due) return 'Due in ' + (due - dom) + ' days';
      if (dom === due) return 'Due today';
      if (dom <= due + 7) return 'Overdue (grace)';
      return 'Overdue';
    },
    deadlineClass(d) {
      const s = this.deadlineState(d);
      if (s === 'Due today' || s.startsWith('Overdue')) return 'moh-deadline-overdue';
      if (s.startsWith('Due in')) return 'moh-deadline-soon';
      return 'moh-deadline-upcoming';
    },
    async lockForm() {
      if (!confirm('Lock this form? Snapshot saved; data will not regenerate.')) return;
      const snapshot = JSON.stringify({ title: this.data.title, sections: this.data.sections, period: this.data.period });
      const r = await executeJson({ function: 'lockMohForm', form_code: this.formCode, year: this.year, month: this.month, snapshot_json: snapshot }, hms);
      if (r.data && r.data.success) await this.load();
    },
    async submitForm() {
      if (!confirm('Mark this form as submitted to the county?')) return;
      const r = await executeJson({ function: 'submitMohForm', form_code: this.formCode, year: this.year, month: this.month, submitted_method: 'manual_upload' }, hms);
      if (r.data && r.data.success) await this.load();
    },
    exportCsv() {
      if (!this.data) return;
      const lines = [];
      lines.push(['Form', this.data.title].join(','));
      lines.push(['Period', this.data.period.label].join(','));
      lines.push(['']);
      this.data.sections.forEach(s => {
        lines.push([s.label]);
        lines.push(['Code', 'Indicator', 'Value', 'Note']);
        s.rows.forEach(r => {
          lines.push([r.code, '"' + (r.label || '').replace(/"/g,'""') + '"', r.value, '"' + (r.note || '').replace(/"/g,'""') + '"'].join(','));
        });
        lines.push(['']);
      });
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = this.formCode + '_' + this.year + '_' + String(this.month).padStart(2,'0') + '.csv';
      a.click(); URL.revokeObjectURL(url);
    },
    printForm() { window.print(); }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.moh-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.moh-input { padding: 7px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; background: #fff; }

.moh-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 14px; flex-wrap: wrap; }
.moh-tab { padding: 10px 14px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 13px; display: inline-flex; align-items: center; gap: 6px; }
.moh-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.moh-header { display: flex; justify-content: space-between; align-items: center; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px 18px; margin-bottom: 14px; flex-wrap: wrap; gap: 12px; }
.moh-title { margin: 0; font-size: 18px; color: #2d3748; }
.moh-muted { color: #a0aec0; font-size: 12px; margin-top: 2px; }
.moh-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

.moh-pill { display: inline-block; padding: 3px 10px; border-radius: 10px; font-size: 11px; font-weight: 600; text-transform: capitalize; }
.moh-pill-info    { background: #bee3f8; color: #2a4365; }
.moh-pill-warn    { background: #feebc8; color: #7b341e; }
.moh-pill-success { background: #c6f6d5; color: #22543d; }
.moh-pill-muted   { background: #edf2f7; color: #4a5568; }

.moh-deadline-panel { background: #f7f8fb; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; margin: 12px 0 18px; }
.moh-deadline-head { font-size: 11pt; color: #2d3748; margin-bottom: 8px; }
.moh-deadline-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 8px; }
.moh-deadline-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px; font-size: 10pt; }
.moh-deadline-code { font-weight: 700; color: #0047AB; font-size: 11pt; }
.moh-deadline-name { color: #4a5568; margin-top: 2px; }
.moh-deadline-due { color: #718096; font-size: 9pt; margin-top: 4px; }
.moh-deadline-state { margin-top: 6px; display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 9pt; font-weight: 600; }
.moh-deadline-overdue { background: rgba(220,53,69,.12); color: #c53030; }
.moh-deadline-soon    { background: rgba(245,158,11,.12); color: #b45309; }
.moh-deadline-upcoming { background: rgba(16,185,129,.12); color: #047857; }

.moh-section { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; margin-bottom: 12px; overflow: hidden; }
.moh-section-head { padding: 10px 14px; background: #f7fafc; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; font-size: 14px; }
.moh-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.moh-table th { background: #f7fafc; padding: 8px 12px; font-weight: 600; color: #4a5568; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 1px solid #e2e8f0; }
.moh-table td { padding: 7px 12px; border-bottom: 1px solid #edf2f7; }
.moh-table tr:last-child td { border-bottom: none; }
.moh-code { color: #718096; font-family: monospace; font-size: 12px; }
.moh-value { text-align: right; font-weight: 700; font-size: 15px; font-variant-numeric: tabular-nums; }
.moh-value-has { color: #2b6cb0; }
.moh-value-zero { color: #cbd5e0; }
.moh-note { color: #a0aec0; font-size: 11px; }

.moh-footer { margin-top: 16px; padding: 14px 16px; background: #fffbeb; border-left: 4px solid #d97706; color: #78350f; font-size: 12px; border-radius: 4px; }
.moh-footer p { margin: 0; }

.moh-empty { padding: 60px 20px; text-align: center; color: #a0aec0; }
.moh-empty i { font-size: 36px; }

.moh-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; text-decoration: none; }
.moh-btn-sm { padding: 5px 10px; font-size: 12px; }
.moh-btn-blue    { background: #2b6cb0; color: #fff; }
.moh-btn-success { background: #38a169; color: #fff; }
.moh-btn-secondary { background: #edf2f7; color: #2d3748; border: 1px solid #cbd5e0; }

@media print {
  .moh-tab-row, .moh-actions, .moh-btn, .moh-input { display: none !important; }
  .moh-section { break-inside: avoid; border: 1px solid #000; }
}
</style>
